import { getDashboardSections } from '../../config/uiConfig';

/**
 * Map a dashboard section to the groupData array that backs it and the
 * icon/label used to present its insight tile.
 */
const SECTION_META = {
  event: { itemsKey: 'events', label: 'Events', icon: 'schedule' },
  resource: { itemsKey: 'resources', label: 'Resources', icon: 'layers' },
  complexDataType: {
    itemsKey: 'complexDataTypes',
    label: 'Complex Data Types',
    icon: 'structure',
  },
  request: { itemsKey: 'requests', label: 'Requests', icon: 'send' },
  requests: { itemsKey: 'requests', label: 'Requests', icon: 'send' },
  response: { itemsKey: 'responses', label: 'Responses', icon: 'reply' },
  responses: { itemsKey: 'responses', label: 'Responses', icon: 'reply' },
  command: { itemsKey: 'commands', label: 'Commands', icon: 'terminal' },
  commands: { itemsKey: 'commands', label: 'Commands', icon: 'terminal' },
  acknowledgement: {
    itemsKey: 'acknowledgements',
    label: 'Acknowledgements',
    icon: 'check_circle',
  },
  type: { itemsKey: 'types', label: 'Types', icon: 'category' },
  types: { itemsKey: 'types', label: 'Types', icon: 'category' },
  auxiliaries: { itemsKey: 'auxiliaries', label: 'Auxiliaries', icon: 'extension' },
  // `label` is a function when the tile title depends on how many items the
  // sidebar lists (one guide vs many).
  fhirIg: {
    itemsKey: 'fhirIgs',
    label: (count) => (count === 1 ? 'FHIR Implementation Guide' : 'FHIR Implementation Guides'),
    icon: 'layers',
  },
};

const asArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Count the entities in a section list plus a few derived facts that make the
 * empty state feel informative: how many carry an explicit schema version and
 * how many distinct sub-groups (parents) they span.
 *
 * @param {Array<{name?: string, parent?: string, version?: string|null}>} items
 * @returns {{ total: number, versioned: number, subGroups: number }}
 */
export function summarizeEntities(items) {
  const list = asArray(items);
  const parents = new Set();
  let versioned = 0;

  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    if (item.version) versioned += 1;
    if (item.parent) parents.add(item.parent);
  }

  return { total: list.length, versioned, subGroups: parents.size };
}

/**
 * Build the insight tiles shown on the dashboard before a schema is selected.
 *
 * The set of tiles is driven by the group's configured dashboard sections, so a
 * latest-only group (e.g. DiabetesCare, resources only) shows only the tiles it
 * actually has, while a full-featured group (e.g. Lab) shows events, resources
 * and complex data types.
 *
 * @param {object} groupData - shape from GroupDataContext
 * @returns {{
 *   groupName: string,
 *   subGroupCount: number,
 *   totalEntities: number,
 *   tiles: Array<{ id: string, label: string, icon: string, count: number, versioned: number, subGroups: number }>,
 * }}
 */
export function computeGroupInsights(groupData) {
  const data = groupData && typeof groupData === 'object' ? groupData : {};
  const groupName = data.groupName || '';
  const sections = getDashboardSections(groupName);

  const seen = new Set();
  const tiles = [];

  for (const section of asArray(sections)) {
    if (!section?.type || seen.has(section.type)) continue;
    // Fall back to the config entry itself so a section added to uiConfig.json
    // still gets a tile before it is given explicit label/icon metadata.
    const meta = SECTION_META[section.type] || {
      itemsKey: section.itemsSource,
      label: section.title || section.type,
      icon: 'layers',
    };
    if (!meta.itemsKey) continue;
    seen.add(section.type);

    const stats = summarizeEntities(data[meta.itemsKey]);
    tiles.push({
      id: section.type,
      label: typeof meta.label === 'function' ? meta.label(stats.total) : meta.label,
      icon: meta.icon,
      count: stats.total,
      subGroups: stats.subGroups,
    });
  }

  const totalEntities = tiles.reduce((sum, tile) => sum + tile.count, 0);
  const subGroupCount = asArray(data.subGroups).length;

  return { groupName, subGroupCount, totalEntities, tiles };
}
